import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { toast } from "sonner";
import { ApprovalActionModal } from "@/components/approval/ApprovalActionModal";
import { ApprovalRequestsFilterOffcanvas } from "@/components/approval/ApprovalRequestsFilterOffcanvas";
import { ApprovalRequestsToolbar } from "@/components/approval/ApprovalRequestsToolbar";
import { ApprovalSelectionBar } from "@/components/approval/ApprovalSelectionBar";
import { InvoiceDataTable } from "@/components/invoices/InvoiceDataTable";
import { ActiveFilterChips } from "@/components/common/ActiveFilterChips";
import { Button } from "@/components/ui/button";
import { selectCurrentUser } from "@/features/auth/authSlice";
import { PERMISSIONS } from "@/features/auth/permissions";
import { useHasPermission } from "@/features/auth/useAuth";
import { useAssignees } from "@/features/invoices/useAssignees";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useErrorToast } from "@/hooks/use-error-toast";
import {
  cloneApprovalRequestFilters,
  countApprovalRequestFilters,
} from "@/lib/approvalRequests";
import { describeApprovalRequestFilters } from "@/lib/activeFilters";
import { formatInvoiceCount } from "@/lib/invoiceStatus";
import { readPersisted, writePersisted } from "@/lib/persistedState";
import { useGetApprovalRequestsQuery } from "@/services/approvals/approvalsApi";
import {
  useApproveInvoiceMutation,
  useAssignInvoiceMutation,
  useGetReviewTypesQuery,
  useRejectInvoiceMutation,
} from "@/services/invoices/invoicesApi";
import { cn } from "@/lib/utils";

const PAGE_SIZE_OPTIONS = [8, 16, 24, 50];
const FILTERS_KEY = "filters.approval-requests";

export function ApprovalRequestsPage() {
  const currentUser = useSelector(selectCurrentUser);
  const can = useHasPermission();
  const showErrorToast = useErrorToast();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(16);
  const [searchValue, setSearchValue] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState(() =>
    cloneApprovalRequestFilters(readPersisted(FILTERS_KEY)),
  );
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [action, setAction] = useState(null);

  const debouncedSearch = useDebouncedValue(searchValue.trim(), 350);

  const queryArgs = useMemo(
    () => ({
      page: pageNumber,
      pageSize,
      query: debouncedSearch || undefined,
      filters,
      currentUserGuid: currentUser?.guid,
    }),
    [pageNumber, pageSize, debouncedSearch, filters, currentUser?.guid],
  );

  const { data, isLoading, isFetching, error } = useGetApprovalRequestsQuery(queryArgs);
  // Memoised so the fallback array does not get a new identity every render and
  // invalidate everything downstream that depends on it.
  const invoices = useMemo(() => data?.items ?? [], [data]);
  const pagination = data?.pagination ?? { total: 0, totalPages: 0 };

  useEffect(() => {
    if (error) showErrorToast(error);
  }, [error, showErrorToast]);

  /* -------------------------------------------------------------- mutations */

  // Reaching the queue means you may *see* what awaits approval; acting on it is
  // a separate grant. Both were previously implied by the route guard alone.
  const canApproveInvoice = can(PERMISSIONS.APPROVE_INVOICE);
  const canRejectInvoice = can(PERMISSIONS.REJECT_INVOICE);
  const { users, request: requestAssignees } = useAssignees({
    eager: Boolean(filters.assignedToId || filters.createdById || filters.updatedById),
  });

  // Only to name a selected review type in its chip; the filter panel loads its
  // own copy while it is open.
  const { data: reviewTypes = [] } = useGetReviewTypesQuery(undefined, {
    skip: !filters.reviewTypeIds?.length,
  });

  const applyFilters = useCallback((next) => {
    setFilters(next);
    writePersisted(FILTERS_KEY, next);
    setPageNumber(1);
  }, []);
  const [assignInvoice] = useAssignInvoiceMutation();
  const [approveInvoice] = useApproveInvoiceMutation();
  const [rejectInvoice] = useRejectInvoiceMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAssigneeChange = async (invoice, userGuid) => {
    try {
      await assignInvoice({ guid: invoice.id, userGuid }).unwrap();
      toast.success(userGuid ? "Invoice assigned" : "Invoice unassigned");
    } catch (assignError) {
      showErrorToast(assignError);
    }
  };

  /**
   * Approve/reject runs one request per invoice; `allSettled` means a single
   * failure does not abandon the rest. The old app additionally spliced rows out
   * of local state and forced a refetch — the cache tags already do both.
   */
  const submitAction = useCallback(
    async ({ reason, comment }) => {
      if (!action) return;
      setIsSubmitting(true);

      const run = (guid) =>
        action.mode === "approve"
          ? approveInvoice({ guid, comment }).unwrap()
          : rejectInvoice({ guid, comment, reason }).unwrap();

      try {
        const results = await Promise.allSettled(action.targets.map((item) => run(item.id)));
        const failed = results.filter((result) => result.status === "rejected").length;
        const succeeded = results.length - failed;
        const verb = action.mode === "approve" ? "approved" : "rejected";

        if (succeeded > 0) {
          toast.success(`${succeeded} invoice${succeeded === 1 ? "" : "s"} ${verb}`);
          setSelectedIds(new Set());
        }
        if (failed > 0) {
          toast.error(`Could not ${action.mode} ${failed} invoice${failed === 1 ? "" : "s"}`);
        }
        setAction(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [action, approveInvoice, rejectInvoice],
  );

  /* -------------------------------------------------------------- selection */

  // Derived rather than pruned in an effect: selection means "rows visible right
  // now", so a row that a filter removed cannot be acted on invisibly.
  const visibleSelectedIds = useMemo(() => {
    if (selectedIds.size === 0) return selectedIds;
    return new Set(invoices.filter((invoice) => selectedIds.has(invoice.id)).map((i) => i.id));
  }, [invoices, selectedIds]);

  const selectedCount = visibleSelectedIds.size;

  const toggleRow = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((current) => {
      const pageIds = invoices.map((invoice) => invoice.id);
      const allSelected = pageIds.length > 0 && pageIds.every((id) => current.has(id));
      const next = new Set(current);
      pageIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const openBulk = (mode) => {
    const targets = invoices.filter((invoice) => visibleSelectedIds.has(invoice.id));
    if (targets.length > 0) setAction({ mode, targets });
  };

  /* ------------------------------------------------------------- pagination */

  const totalPages = Math.max(1, pagination.totalPages || 1);
  // Clamped on read, so no effect is needed to correct the page number after a
  // filter shrinks the result set.
  const currentPage = Math.min(pageNumber, totalPages);
  const resetToFirstPage = () => setPageNumber(1);

  return (
    <div className="content-stack flex h-full min-h-0 flex-1 flex-col">
      <section className={cn("page-panel relative", selectedCount > 0 && "pb-2")}>
        <ApprovalRequestsToolbar
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
            resetToFirstPage();
          }}
          isSearching={isFetching && !isLoading}
          onFilterClick={() => setFilterOpen(true)}
          filterOpen={filterOpen}
          filterCount={countApprovalRequestFilters(filters)}
        />

        <ApprovalRequestsFilterOffcanvas
          open={filterOpen}
          onOpenChange={setFilterOpen}
          value={filters}
          onApply={applyFilters}
        />

        <ActiveFilterChips
          chips={describeApprovalRequestFilters(filters, { users, reviewTypes })}
          onRemove={(chip) =>
            applyFilters(cloneApprovalRequestFilters({ ...filters, ...chip.patch }))
          }
          onClearAll={() => applyFilters(cloneApprovalRequestFilters())}
        />

        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col overflow-hidden",
            selectedCount > 0 && "pb-14",
          )}
        >
          <InvoiceDataTable
            variant="approval-requests"
            invoices={invoices}
            isLoading={isLoading}
            selectedIds={visibleSelectedIds}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
            assignees={users}
            onRequestAssignees={requestAssignees}
            onAssigneeChange={handleAssigneeChange}
            onApprove={
              canApproveInvoice
                ? (invoice) => setAction({ mode: "approve", targets: [invoice] })
                : undefined
            }
            onReject={
              canRejectInvoice
                ? (invoice) => setAction({ mode: "reject", targets: [invoice] })
                : undefined
            }
            caption="Approval requests"
          />
        </div>

        <ApprovalSelectionBar
          count={selectedCount}
          onClear={() => setSelectedIds(new Set())}
          onApprove={() => openBulk("approve")}
          onReject={() => openBulk("reject")}
          canApprove={canApproveInvoice}
          canReject={canRejectInvoice}
        />

        <div className="page-footer">
          <label className="flex items-center gap-2 text-sm">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                resetToFirstPage();
              }}
              className="num h-8 rounded-lg border-0 bg-muted px-2 text-sm text-foreground"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <nav aria-label="Pagination" className="flex items-center gap-1">
            <span className="num me-2 text-sm" aria-live="polite">
              {formatInvoiceCount(pagination.total)}
              {pagination.total > 0 ? ` · Page ${currentPage}/${totalPages}` : ""}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="touch-target-icon hidden bg-muted shadow-none sm:inline-flex"
              disabled={currentPage <= 1 || isFetching}
              onClick={() => setPageNumber(1)}
              aria-label="First page"
            >
              <ChevronsLeft />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="touch-target-icon bg-muted shadow-none"
              disabled={currentPage <= 1 || isFetching}
              onClick={() => setPageNumber((page) => page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="touch-target-icon bg-muted shadow-none"
              disabled={currentPage >= totalPages || isFetching}
              onClick={() => setPageNumber((page) => page + 1)}
              aria-label="Next page"
            >
              <ChevronRight />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="touch-target-icon hidden bg-muted shadow-none sm:inline-flex"
              disabled={currentPage >= totalPages || isFetching}
              onClick={() => setPageNumber(totalPages)}
              aria-label="Last page"
            >
              <ChevronsRight />
            </Button>
          </nav>
        </div>
      </section>

      <ApprovalActionModal
        open={action != null}
        onOpenChange={(open) => !open && setAction(null)}
        mode={action?.mode ?? "approve"}
        count={action?.targets.length ?? 0}
        isSubmitting={isSubmitting}
        onSubmit={submitAction}
      />
    </div>
  );
}
